/**
 * Consent class to handle user consent for functional cookies.
 * 
 * This class is responsible for managing user consent for functional cookies,
 * monitoring changes to the consent status, and providing methods to set or revoke
 * consent for testing purposes. It uses a polling mechanism to detect cookie changes
 * and triggers custom events when the consent status is updated.
 * 
 * Dependencies:
 * - jQuery: Used for triggering custom events (`consentChanged`).
 * - Cookie: A utility class for managing cookies.
 * 
 * Example Usage:
 * - Instantiate the class to automatically start monitoring cookie changes:
 *   ```javascript
 *   const consent = new Consent();
 *   ```
 * - Check if functional consent is granted:
 *   ```javascript
 *   if (consent.hasFunctionalConsent()) {
 *       console.log('Consent granted');
 *   }
 *   ```
 * - Set or revoke consent for testing:
 *   ```javascript
 *   consent.setFunctionalConsent();
 *   consent.revokeFunctionalConsent();
 *   ```
 * 
 * @file Manages user consent for functional cookies and monitors changes in consent status.
 */

import Cookie from './cookie';

class Consent {
    /**
     * Constructor to initialize the Consent class.
     * Sets up initial state and starts polling for cookie changes.
     */
    constructor() {
        this.cookieName = 'CookieConsent';
        this.previousCookies = document.cookie;
        this.pollInterval = 1000;
        this.consentGranted = this.checkForFunctionalConsent();

        // Start polling for changes in consent status
        this.startPolling();
    }

    /**
     * Checks if functional consent has been granted by reading the consent cookie.
     * @returns {boolean} - True if functional consent is granted, false otherwise.
     */
    checkForFunctionalConsent() {
        const cookieValue = Cookie.get(this.cookieName);
        return cookieValue && cookieValue.includes('functional:granted');
    }

    /**
     * Starts a polling mechanism to check for cookie changes at regular intervals.
     * @returns {void}
     */
    startPolling() {
        setInterval(() => this.checkCookieChanges(), this.pollInterval);
    }

    /**
     * Checks for changes in cookies and updates the consent status if necessary.
     * Triggers a 'consentChanged' event when the consent status changes.
     * @returns {void}
     */
    checkCookieChanges() {
        const currentCookies = document.cookie;

        // If the consent status has changed, update and trigger an event
        if (currentCookies !== this.previousCookies) {
            this.previousCookies = currentCookies;
            const newConsentStatus = this.checkForFunctionalConsent();

            // If the consent status has changed, update and dispatch an event
            if (newConsentStatus !== this.consentGranted) {
                this.consentGranted = newConsentStatus;
                const event = new CustomEvent('consentChanged', {
                    detail: { newConsentStatus }
                });
                document.dispatchEvent(event);
            }
        }
    }

    /**
     * Public method to get the current consent status.
     * @returns {boolean} - True if functional consent is granted, false otherwise.
     */
    hasFunctionalConsent() {
        return this.consentGranted;
    }

    /**
     * Function to manually set functional consent for testing purposes.
     * Sets the consent cookie and triggers the necessary updates.
     * @returns {void}
     */
    setFunctionalConsent() {
        Cookie.set(this.cookieName, 'consent:provided|performance:granted|functional:granted|targeting:granted|allcookies:accepted', 7);
        this.consentGranted = true;

        const event = new CustomEvent('consentChanged', {
            detail: { newConsentStatus: true },
        });
        document.dispatchEvent(event);
    }

    /**
     * Function to manually revoke functional consent for testing purposes.
     * Deletes the consent cookie and triggers the necessary updates.
     * @returns {void}
     */
    revokeFunctionalConsent() {
        Cookie.delete(this.cookieName);
        this.consentGranted = false;

        const event = new CustomEvent('consentChanged', {
            detail: { newConsentStatus: false },
        });
        document.dispatchEvent(event);
    }
}

export default Consent;